import React from "react";
import { Clock, CheckCircle, ArrowRight } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { useInteractionTracking } from "../../hooks/useAnalytics";
import welcomeImg from "../../assets/images/welcome-img.svg";

const RenewalWelcomeScreen = ({ userData, onComplete, onReset }) => {
  const { trackButtonClick } = useInteractionTracking();

  // Renewal-specific journey steps
  const renewalSteps = [
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Review your verified information",
      description: "Confirm your existing details",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Confirm a few quick details",
      description: "Ensure your profile is up-to-date",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Find savings before your renewal",
      description: "Discover competitive insurance offers",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Explore options from trusted partners",
      description: "Compare personalized insurance solutions",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Earn from your Ownli Insurance Profile",
      description: "Get rewarded for your insurance journey",
    },
  ];

  return (
    <div className="space-y-8 p-4 pt-5 pb-6">
      {/* Renewal Header */}
      <div className="text-center bg-white p-5 rounded-[20px]">
        <h1 className="font-bebasNeue text-3xl font-bold text-gray-900 mb-2">
          Check your insurance price before renewal
        </h1>
        <p className="text-lg text-gray-600">
          You are entering the best window to compare options. Since your insurance profile is already verified with Ownli.
        </p>
        <img className="mx-auto mt-5 max-w-[250px]" src={welcomeImg} alt="Renewal illustration" />
      </div>

      {/* Time Commitment Card */}
      <Card className="bg-greenish border-none !mt-6 rounded-[20px]">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <Clock className="min-w-[25px] min-h-[25px] text-green" />
            <p className="text-base text-darkGrey">
              Takes less than 3 minutes to complete renewal
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Renewal Process Steps */}
      <Card className="bg-white border-none rounded-[20px] !mt-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">
            Renewal Process:
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {renewalSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="mt-2 min-w-[10px] min-h-[10px] bg-darkGreen rounded-full"></span>
                <div>
                  <h3 className="font-base text-darkGrey">{step.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTAs */}
      <div className="space-y-3">
        <button
          onClick={() => {
            trackButtonClick("renewal_get_started", {
              button_text: "START NOW",
            });
            onComplete();
          }}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
        >
          START NOW
          <ArrowRight className="w-4 h-4" />
        </button>

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli's{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Privacy Policy
          </a>{" "}
          .
        </div>
      </div>
    </div>
  );
};

export default RenewalWelcomeScreen;